import { createContext, useContext, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { PlanFeatures, MenuItem, LimitKey, ToggleKey } from "@shared/schema";

interface PlanContextData {
  planFeatures: PlanFeatures | null;
  counts: {
    goals: number;
    tasks: number;
    habits: number;
    milestones: number;
    journal_entries: number;
    notes: number;
    checklists: number;
    media_files: number;
  };
  isLoading: boolean;
  hasFeature: (featureKey: MenuItem) => boolean;
  hasToggle: (toggleKey: ToggleKey) => boolean;
  getLimit: (limitKey: LimitKey) => { max: number; current: number; remaining: number };
}

const PlanContext = createContext<PlanContextData | undefined>(undefined);

export function PlanProvider({ children }: { children: ReactNode }) {
  const { data, isLoading } = useQuery<{
    planFeatures: PlanFeatures;
    counts: {
      goals: number;
      tasks: number;
      habits: number;
      milestones: number;
      journal_entries: number;
      notes: number;
      checklists: number;
      media_files: number;
    };
  }>({
    queryKey: ["/api/user/plan-features"],
  });

  const hasFeature = (featureKey: MenuItem): boolean => {
    if (!data?.planFeatures) return true; // Default to allowing if no plan data
    return data.planFeatures.menuAccess[featureKey] || false;
  };

  const hasToggle = (toggleKey: ToggleKey): boolean => {
    if (!data?.planFeatures) return false;
    return data.planFeatures.toggles[toggleKey] || false;
  };

  const getLimit = (limitKey: LimitKey) => {
    if (!data?.planFeatures) {
      return { max: 0, current: 0, remaining: 0 };
    }

    const max = data.planFeatures.limits[limitKey] || 0;
    
    // Map limit keys to count keys
    const countKeyMap: Record<LimitKey, keyof typeof data.counts> = {
      max_goals: 'goals',
      max_tasks: 'tasks',
      max_habits: 'habits',
      max_milestones: 'milestones',
      max_journal_entries: 'journal_entries',
      max_notes: 'notes',
      max_checklists: 'checklists',
      max_media_files: 'media_files',
    };

    const current = data.counts[countKeyMap[limitKey]] || 0;
    const remaining = Math.max(0, max - current);

    return { max, current, remaining };
  };

  return (
    <PlanContext.Provider
      value={{
        planFeatures: data?.planFeatures || null,
        counts: data?.counts || {
          goals: 0,
          tasks: 0,
          habits: 0,
          milestones: 0,
          journal_entries: 0,
          notes: 0,
          checklists: 0,
          media_files: 0,
        },
        isLoading,
        hasFeature,
        hasToggle,
        getLimit,
      }}
    >
      {children}
    </PlanContext.Provider>
  );
}

export function usePlanContext(): PlanContextData {
  const context = useContext(PlanContext);
  if (!context) {
    throw new Error("usePlanContext must be used within PlanProvider");
  }
  return context;
}

export function useFeature(featureKey: MenuItem): boolean {
  const { hasFeature } = usePlanContext();
  return hasFeature(featureKey);
}

export function useToggle(toggleKey: ToggleKey): boolean {
  const { hasToggle } = usePlanContext();
  return hasToggle(toggleKey);
}

export function useLimit(limitKey: LimitKey) {
  const { getLimit } = usePlanContext();
  return getLimit(limitKey);
}
